/**
 * Image Caching Utilities
 * 
 * Provides utilities for preloading and caching images to improve
 * performance and reduce loading times on subsequent visits.
 */

import { useEffect } from 'react';

/**
 * Preload a single image
 * @param {string} src - Image URL to preload
 * @returns {Promise} - Resolves when image is loaded
 */
export const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('Image source is required'));
      return;
    }

    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (error) => reject(error);
    img.src = src;
  });
};

/**
 * Preload multiple images
 * @param {Array<string>} srcs - Array of image URLs to preload
 * @returns {Promise<Array>} - Resolves when all images are loaded
 */
export const preloadImages = (srcs) => {
  if (!Array.isArray(srcs) || srcs.length === 0) {
    return Promise.resolve([]);
  }

  return Promise.allSettled(srcs.map((src) => preloadImage(src)));
};

/**
 * Preload images for menu items
 * @param {Array} items - Menu items array
 * @param {number} limit - Maximum number of images to preload (default: 10)
 * @returns {Promise<Array>} - Resolves when images are preloaded
 */
export const preloadMenuImages = (items, limit = 10) => {
  if (!Array.isArray(items) || items.length === 0) {
    return Promise.resolve([]);
  }

  // Extract image URLs from items
  const imageUrls = items
    .slice(0, limit)
    .map((item) => item?.item_image_url)
    .filter(Boolean);

  return preloadImages(imageUrls);
};

/**
 * Add cache-busting query parameter to image URL if needed
 * @param {string} url - Original image URL
 * @param {boolean} bustCache - Whether to add cache-busting parameter
 * @returns {string} - Modified URL
 */
export const addCacheBuster = (url, bustCache = false) => {
  if (!url || !bustCache) return url;

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}t=${Date.now()}`;
};

/**
 * Get optimized image URL from Firebase Storage or CDN
 * Adds size parameters for better performance
 * @param {string} url - Original image URL
 * @param {Object} options - Optimization options
 * @param {number} options.width - Desired width
 * @param {number} options.height - Desired height
 * @param {string} options.quality - Image quality (low, medium, high)
 * @returns {string} - Optimized URL
 */
export const getOptimizedImageUrl = (url, options = {}) => {
  if (!url) return url;

  // Check if it's a Firebase Storage URL
  if (url.includes('firebasestorage.googleapis.com')) {
    // Firebase Storage supports some transformations via URL parameters
    const { width, height } = options;
    const params = new URLSearchParams();

    if (width) params.append('w', width);
    if (height) params.append('h', height);

    if (params.toString()) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}${params.toString()}`;
    }
  }

  return url;
};

/**
 * Check if image is cached in browser
 * @param {string} src - Image URL
 * @returns {boolean} - True if image is likely cached
 */
export const isImageCached = (src) => {
  if (!src) return false;

  const img = new Image();
  img.src = src;

  // If complete and has a natural width, it's likely cached
  return img.complete && img.naturalWidth > 0;
};

/**
 * Get image dimensions without loading the full image
 * @param {string} src - Image URL
 * @returns {Promise<{width: number, height: number}>}
 */
export const getImageDimensions = (src) => {
  return new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('Image source is required'));
      return;
    }

    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Custom hook for preloading images on component mount
 * Usage: useImagePreload(['/image1.jpg', '/image2.jpg'])
 * @param {Array<string>} imageSrcs - Array of image URLs to preload
 */
export const useImagePreload = (imageSrcs) => {
  useEffect(() => {
    if (Array.isArray(imageSrcs) && imageSrcs.length > 0) {
      preloadImages(imageSrcs).catch((error) => {
        console.warn('Error preloading images:', error);
      });
    }
  }, [imageSrcs]);
};

export default {
  preloadImage,
  preloadImages,
  preloadMenuImages,
  addCacheBuster,
  getOptimizedImageUrl,
  isImageCached,
  getImageDimensions,
};
